import { useEffect } from 'react'
import { trackEvent } from '../../../../utils/appBridge'
import BottomSheetContent from '../../BottomSheetContent'
import { EventNames } from '../../../../types/misc.types'
import RenderHTML from '../../../ui-components/RenderHTML'
import { PrimaryButton } from '../../../execution/PrimaryButton'
import { oneTimeWithdrawIcon } from '../../../../utils/mf-execution/buttonsConfig'
import { FolioDetail, WithdrawAccountDetail } from '../../../../types/mf-execution.types'
import { mfLogoBaseUrl } from '../../../../env'
import { BreakPoint, TaxInfo } from '../../../../types/mutual-fund-detail.types'
import { calculateTaxLiability } from '../../../../utils/mf-execution/taxLiabilityUtils'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { ExitLoadBottomSheet } from './ExitLoadBottomSheet'
import { TaxLiabilityBottomSheet } from './TaxLiabilityBottomSheet'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'

export const WithdrawConfirmationBottomSheet = ({
  onDismiss,
  ScreenName,
  amount,
  selectedAccount,
  exitLoad,
  exitLoadSlabs,
  stcgInfo,
  ltcgInfo,
  grandfatheringRuleApplicable,
  onPlaceOrder,
  trackingFund,
  withdrawsToFolioLinkedBank
}: {
  onDismiss: () => void
  ScreenName: string
  amount: number
  selectedAccount: WithdrawAccountDetail | null
  selectedFolio: FolioDetail | null
  exitLoad: number
  exitLoadSlabs: BreakPoint[]
  stcgInfo: TaxInfo
  ltcgInfo: TaxInfo
  grandfatheringRuleApplicable: boolean
  onPlaceOrder: () => void
  trackingFund?: { fundIsin: string; fundName: string }
  withdrawsToFolioLinkedBank: boolean
}) => {
  const heading = 'Review details'
  const { openNestedBottomSheet, closeNestedBottomSheet } = useBottomSheet()

  useEffect(() => {
    trackEvent({
      eventName: EventNames.KnowMoreBottomSheetLoad,
      payload: {
        eventId: heading,
        ScreenName,
        ...(trackingFund ?? {})
      }
    })
  }, [])

  return (
    <BottomSheetContent
      heading={heading}
      onDismiss={onDismiss}
      bottomSheetHeadingClassName='!font-[500]'
      bottomSheetBodyClassName='!px-0 !pb-5'
      bottomSheetCtas={
        <PrimaryButton
          onClick={onPlaceOrder}
          label='Confirm withdrawal'
          primaryButtonClassName='!h-[56px] !text-[16px] !font-[500]'
        />
      }
    >
      <div className='flex flex-col pt-6 border-t border-[#F0F4F7]'>
        {/* Withdrawal Details */}
        <div className='flex gap-5 w-full pl-5'>
          <div className='pt-3'>
            <RenderHTML innerHTMLText={oneTimeWithdrawIcon} />
          </div>
          <div className='flex w-1 flex-1'>
            <div className='flex flex-col'>
              <div className='text-[#8E9393] font-[400] text-[13px]'>Withdrawal amount</div>
              <div className='text-[#232323] text-[22px] font-[500] pb-3'>
                {roundOffAndFormatNumberWithCurrency(amount)}
              </div>
              <InfoRow
                label='Exit Load'
                value={roundOffAndFormatNumberWithCurrency(exitLoad)}
                onClick={() =>
                  openNestedBottomSheet(
                    <ExitLoadBottomSheet
                      heading='Exit Load'
                      onDismiss={closeNestedBottomSheet}
                      exitLoad={exitLoad}
                      amount={amount}
                      exitLoadSlabs={exitLoadSlabs}
                    />
                  )
                }
                showInfoIcon
              />
              <InfoRow label='you get' value={roundOffAndFormatNumberWithCurrency(amount - exitLoad)} />
              <InfoRow
                label='Tax Liability'
                value={roundOffAndFormatNumberWithCurrency(
                  calculateTaxLiability(amount, exitLoad, stcgInfo, ltcgInfo).totalTaxLiability
                )}
                onClick={() =>
                  openNestedBottomSheet(
                    <TaxLiabilityBottomSheet
                      heading='Tax Liability'
                      onDismiss={closeNestedBottomSheet}
                      withdrawalAmount={amount}
                      exitLoad={exitLoad}
                      stcgInfo={stcgInfo}
                      ltcgInfo={ltcgInfo}
                      grandfatheringRuleApplicable={grandfatheringRuleApplicable}
                    />
                  )
                }
                showInfoIcon
              />
            </div>
          </div>
        </div>
        <div className='pl-16 py-4'>
          <div className='bg-[#F0F4F7] h-[1px] w-full'></div>
        </div>
        {/* Bank Account */}
        <div className='flex items-center gap-4 w-full pl-5'>
          <div>
            {withdrawsToFolioLinkedBank ? (
              <img
                src={`${mfLogoBaseUrl}/images/mutual_fund/categories/v2/banking_and_psu.svg`}
                alt='Bank linked to this folio'
                className='w-6 h-6'
              />
            ) : (
              <img
                src={`${mfLogoBaseUrl}${selectedAccount?.logoUrl}`}
                alt={selectedAccount?.displayAccNumber}
                className='w-8 h-8'
              />
            )}
          </div>
          <div className='flex w-1 flex-1'>
            <div className='flex flex-col w-full'>
              <div className='text-[#8E9393] text-[13px] font-[400]'>Withdrawing to</div>
              {withdrawsToFolioLinkedBank ? (
                <div className='text-[#232323] text-[16px] font-[500] pt-[2px] truncate max-w-[90%]'>
                  Bank linked to this folio
                </div>
              ) : (
                <div className='text-[#232323] text-[16px] font-[500] pt-[2px] truncate max-w-[90%]'>
                  {selectedAccount?.displayAccNumber}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </BottomSheetContent>
  )
}

export const InfoRow = ({
  label,
  value,
  onClick,
  showInfoIcon = false
}: {
  label: string
  value: string | number
  onClick?: () => void
  showInfoIcon?: boolean
}) => (
  <div className='flex items-center pb-[8px]'>
    <div
      onClick={onClick}
      className={`text-[#6A6E6E] font-[400] text-[13px] w-[100px] ${onClick ? 'flex items-center gap-1 cursor-pointer' : ''}`}
    >
      {label}
      {showInfoIcon && (
        <div className='mt-[1px]'>
          <RenderHTML innerHTMLText={SmallInfoIcon} />
        </div>
      )}
    </div>
    <div className='text-[#232323] text-[13px] font-[500]'>{value}</div>
  </div>
)

const SmallInfoIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <g clip-path="url(#clip0_36697_23566)">
    <path d="M6 0.5C7.90893 0.5 9.26047 0.98508 10.1377 1.8623C11.0149 2.73953 11.5 4.09108 11.5 6C11.5 7.90893 11.0149 9.26047 10.1377 10.1377C9.26047 11.0149 7.90893 11.5 6 11.5C4.09108 11.5 2.73953 11.0149 1.8623 10.1377C0.98508 9.26047 0.5 7.90893 0.5 6C0.5 4.09108 0.985078 2.73953 1.8623 1.8623C2.73953 0.985078 4.09108 0.5 6 0.5Z" stroke="#D1D6D6"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.7825 7.65517C4.50229 7.65517 4.27515 7.87673 4.27515 8.15004C4.27515 8.42337 4.50229 8.64492 4.7825 8.64492H7.21779C7.498 8.64492 7.72515 8.42337 7.72515 8.15004C7.72515 7.87673 7.498 7.65517 7.21779 7.65517H6.5075V5.37873C6.5075 5.10542 6.28035 4.88386 6.00015 4.88386H5.18838C4.90818 4.88386 4.68103 5.10542 4.68103 5.37873C4.68103 5.65205 4.90818 5.87361 5.18838 5.87361H5.49279V7.65517H4.7825ZM6.81191 3.39923C6.81191 3.83652 6.44847 4.19103 6.00015 4.19103C5.55182 4.19103 5.18838 3.83652 5.18838 3.39923C5.18838 2.96193 5.55182 2.60742 6.00015 2.60742C6.44847 2.60742 6.81191 2.96193 6.81191 3.39923Z" fill="#6A6E6E"/>
  </g>
  <defs>
    <clipPath id="clip0_36697_23566">
      <rect width="12" height="12" fill="white"/>
    </clipPath>
  </defs>
</svg>`
